import { useState, useEffect } from 'react'
import { Filter } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export interface AdvancedFilters {
  empresa?: string
  centroCusto?: string
  valorMin?: number
  valorMax?: number
}

interface GerencialAdvancedFiltersPopoverProps {
  value: AdvancedFilters
  onApply: (filters: AdvancedFilters) => void
}

export function GerencialAdvancedFiltersPopover({ value, onApply }: GerencialAdvancedFiltersPopoverProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<AdvancedFilters>(value)

  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const activeCount =
    (value.empresa ? 1 : 0) +
    (value.centroCusto ? 1 : 0) +
    (value.valorMin !== undefined ? 1 : 0) +
    (value.valorMax !== undefined ? 1 : 0)

  const handleApply = () => {
    onApply(draft)
    setOpen(false)
  }
  const handleClear = () => {
    setDraft({})
    onApply({})
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" size="sm">
          <Filter className="w-4 h-4 mr-1" />
          + filtros
          {activeCount > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-brand-primary text-brand-dark text-xs font-bold rounded-full">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-4 bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta space-y-3"
      >
        <h4 className="text-sm font-semibold text-white mb-2">Filtros adicionais</h4>

        <Field label="Empresa">
          <input
            type="text"
            value={draft.empresa ?? ''}
            onChange={e => setDraft(d => ({ ...d, empresa: e.target.value || undefined }))}
            placeholder="Nome ou código"
            className="w-full bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </Field>

        <Field label="Centro de Custo">
          <input
            type="text"
            value={draft.centroCusto ?? ''}
            onChange={e => setDraft(d => ({ ...d, centroCusto: e.target.value || undefined }))}
            placeholder="Centro de Custo"
            className="w-full bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </Field>

        <Field label="Faixa de valor">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              value={draft.valorMin ?? ''}
              onChange={e => setDraft(d => ({
                ...d,
                valorMin: e.target.value === '' ? undefined : Number(e.target.value),
              }))}
              placeholder="Mín R$"
              className="w-full bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
            <input
              type="number"
              value={draft.valorMax ?? ''}
              onChange={e => setDraft(d => ({
                ...d,
                valorMax: e.target.value === '' ? undefined : Number(e.target.value),
              }))}
              placeholder="Máx R$"
              className="w-full bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>
        </Field>

        <div className="flex justify-end gap-2 pt-2 border-t border-brand-dark-lighter">
          <Button variant="ghost" size="sm" onClick={handleClear}>
            Limpar
          </Button>
          <Button variant="primary" size="sm" onClick={handleApply}>
            Aplicar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-brand-gray uppercase tracking-wide font-semibold">{label}</label>
      {children}
    </div>
  )
}
